/*
  CompanyProfileSettings.jsx
  Issuer (Absolin) profile — used as the FROM side of every invoice.
*/
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const empty = {
  name: "Absolin Software Solutions LLP",
  address: "",
  state: "Andhra Pradesh",
  state_code: "37",
  gstin: "",
  phone: "",
  email: "",
  logo_url: "",
  signature_url: "",
  stamp_url: "",
  dsc_signer_name: "",
  bank_account_name: "",
  bank_account_no: "",
  bank_ifsc: "",
  bank_branch: "",
  upi_id: "",
  terms_and_conditions: "",
  footer_note: "* This is computer generated invoice, for any clarification please contact ABSOLIN - Admin/Accounts team",
};

export default function CompanyProfileSettings() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/collections/company-profile");
        setForm({ ...empty, ...(r.data || {}) });
      } catch { /* keep defaults */ }
      setLoading(false);
    })();
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/collections/company-profile", form);
      toast.success("Company profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (loading) return <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>;

  return (
    <Card data-testid="company-profile-panel">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b">
          <Building2 className="w-5 h-5 text-[#0066CC]" />
          <div>
            <h3 className="text-sm font-bold text-slate-800">Company Profile</h3>
            <p className="text-[10.5px] text-slate-500">Issuer details printed on every Proforma & Tax Invoice.</p>
          </div>
        </div>

        <section>
          <p className="text-[11px] font-semibold text-slate-700 mb-2 uppercase tracking-wider">Business</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Legal Name *</Label><Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="cp-name" /></div>
            <div><Label className="text-xs">GSTIN *</Label><Input value={form.gstin} onChange={e => set("gstin", e.target.value.toUpperCase())} placeholder="37ABAFA0565F2ZS" data-testid="cp-gstin" /></div>
            <div className="md:col-span-2"><Label className="text-xs">Full Address</Label><Textarea rows={2} value={form.address} onChange={e => set("address", e.target.value)} data-testid="cp-address" /></div>
            <div><Label className="text-xs">State</Label><Input value={form.state} onChange={e => set("state", e.target.value)} data-testid="cp-state" /></div>
            <div><Label className="text-xs">State Code (2-digit) *</Label><Input value={form.state_code} onChange={e => set("state_code", e.target.value.slice(0, 2))} placeholder="37" data-testid="cp-state-code" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="cp-phone" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="cp-email" /></div>
          </div>
        </section>

        <section>
          <p className="text-[11px] font-semibold text-slate-700 mb-2 uppercase tracking-wider">Bank / Payment</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Account Name</Label><Input value={form.bank_account_name} onChange={e => set("bank_account_name", e.target.value)} data-testid="cp-bank-name" /></div>
            <div><Label className="text-xs">Account No.</Label><Input value={form.bank_account_no} onChange={e => set("bank_account_no", e.target.value)} data-testid="cp-bank-acct" /></div>
            <div><Label className="text-xs">IFSC Code</Label><Input value={form.bank_ifsc} onChange={e => set("bank_ifsc", e.target.value.toUpperCase())} data-testid="cp-bank-ifsc" /></div>
            <div><Label className="text-xs">Branch</Label><Input value={form.bank_branch} onChange={e => set("bank_branch", e.target.value)} data-testid="cp-bank-branch" /></div>
            <div className="md:col-span-2"><Label className="text-xs">UPI ID</Label><Input value={form.upi_id} onChange={e => set("upi_id", e.target.value)} placeholder="9989031011@hdfc" data-testid="cp-upi" /></div>
          </div>
        </section>

        <section>
          <p className="text-[11px] font-semibold text-slate-700 mb-2 uppercase tracking-wider">Branding (optional overrides)</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="text-xs">Logo URL</Label><Input value={form.logo_url} onChange={e => set("logo_url", e.target.value)} placeholder="https://…" data-testid="cp-logo" /></div>
            <div><Label className="text-xs">Signature URL</Label><Input value={form.signature_url} onChange={e => set("signature_url", e.target.value)} placeholder="https://…" data-testid="cp-signature" /></div>
            <div><Label className="text-xs">Stamp URL</Label><Input value={form.stamp_url} onChange={e => set("stamp_url", e.target.value)} placeholder="https://…" data-testid="cp-stamp" /></div>
            <div className="md:col-span-2"><Label className="text-xs">DSC Signer Name <span className="text-slate-400">(shown inside the Digital Signature panel on invoices)</span></Label><Input value={form.dsc_signer_name} onChange={e => set("dsc_signer_name", e.target.value)} placeholder="e.g. Abdul Latif · Managing Partner" data-testid="cp-dsc-signer" /></div>
          </div>
          <p className="text-[10px] text-slate-400 mt-1">Leave blank to use the built-in Absolin logo / signature / stamp.</p>
        </section>

        <section>
          <Label className="text-xs">Terms &amp; Conditions</Label>
          <Textarea rows={5} value={form.terms_and_conditions || ""} onChange={e => set("terms_and_conditions", e.target.value)} placeholder={"One term per line. Leave blank to use defaults.\nExample:\nPayment due within 15 days.\nInterest @ 18% p.a. on overdue.\nSubject to Visakhapatnam jurisdiction only."} className="text-[11px] font-mono" data-testid="cp-terms" />
          <p className="text-[10px] text-slate-400 mt-1">Each line becomes a numbered clause on the invoice.</p>
        </section>

        <section>
          <Label className="text-xs">Footer Note</Label>
          <Textarea rows={2} value={form.footer_note} onChange={e => set("footer_note", e.target.value)} data-testid="cp-footer" />
        </section>

        <div className="flex justify-end pt-2 border-t">
          <Button onClick={save} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2" data-testid="cp-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Profile
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
